import React, { useState, useCallback, useRef } from 'react';
import {
  EnvironmentOutlined,
  TeamOutlined,
  GlobalOutlined,
  TagOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { useMediaQuery } from 'react-responsive';

// COMPONENTS
import TargetingLocations from './TargetingLocations';
import TargetingLocationAudiences from './TargetingLocationAudiences';
import RegionGroup from '../RegionGroup';
import { TargetingDealIds } from './TargetingDealIds';

const TAB_CONFIG = [
  {
    key: 'location',
    label: 'Addresses',
    icon: EnvironmentOutlined,
  },
  {
    key: 'audience',
    label: 'Audiences',
    icon: TeamOutlined,
  },
  {
    key: 'region-group',
    label: 'Region Groups',
    icon: GlobalOutlined,
    conditional: true,
  },
  {
    key: 'deal-ids',
    label: 'Deal IDs',
    icon: TagOutlined,
  },
];

export default function TargetingLocationTab({
  attributionAudienceName,
  createTargetingLocation,
  deleteLocation,
  google,
  locationList,
  onChangeStateValue,
  onCsvUploadComplete,
  onSubmitAudience,
  onAddToExistingAudience,
  selectedLocations,
  updateTargetingLocation,
  isTargetingPreviewModalOpen,
  targetingLocationsPreviewData,
  closeTargetingPreviewModal,
  bulkImportTargetingLocations,
  selectTargetingLocationPreview,
  targetingAudiences,
  deleteAudienceLocation,
  deleteBulkLocation,
  handleSubmitRegionGroup,
  handleUpdateRegionGroup,
  deleteRegionGroup,
  regionGroups,
  selectedOrg,
  isSelfService,
  setAudienceAmplifyDefault,
  isPreviewOrg,
  amplify,
  dealIds,
  createDealId,
  deleteDealIds,
  updateDealId,
  presetRegionGroups,
  presetRegionGroupsLoading,
  currentuser,
}) {
  const [activeTab, setActiveTab] = useState('location');
  const [toolbarContainer, setToolbarContainer] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navRef = useRef(null);
  const dropdownRef = useRef(null);

  const useDropdown = useMediaQuery({ query: '(max-width: 1337px)' });

  // Build visible tabs based on permissions
  const visibleTabs = TAB_CONFIG.filter(
    (tab) => !tab.conditional || isSelfService || isPreviewOrg
  );

  // Count badges per tab
  const getTabCount = useCallback(
    (key) => {
      switch (key) {
        case 'location':
          return locationList
            ? locationList.filter(
                (loc) =>
                  loc &&
                  loc.org &&
                  selectedOrg &&
                  loc.org.id === selectedOrg.id &&
                  loc.audienceType === 'TARGETING'
              ).length
            : 0;
        case 'audience':
          return targetingAudiences
            ? targetingAudiences.filter((a) => a.orgs && a.orgs.length === 1).length
            : 0;
        case 'region-group':
          return regionGroups && regionGroups.regionGroups
            ? regionGroups.regionGroups.length
            : 0;
        case 'deal-ids':
          return dealIds ? dealIds.length : 0;
        default:
          return 0;
      }
    },
    [locationList, targetingAudiences, regionGroups, dealIds, selectedOrg]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      const currentIndex = visibleTabs.findIndex((t) => t.key === activeTab);
      let nextIndex;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          nextIndex = (currentIndex + 1) % visibleTabs.length;
          setActiveTab(visibleTabs[nextIndex].key);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          nextIndex = (currentIndex - 1 + visibleTabs.length) % visibleTabs.length;
          setActiveTab(visibleTabs[nextIndex].key);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          break;
        default:
          break;
      }
    },
    [activeTab, visibleTabs]
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'location':
        return (
          <TargetingLocations
            toolbarContainer={toolbarContainer}
            attributionAudienceName={attributionAudienceName}
            createTargetingLocation={createTargetingLocation}
            deleteLocation={deleteLocation}
            google={google}
            locationList={locationList}
            onChangeStateValue={onChangeStateValue}
            onCsvUploadComplete={onCsvUploadComplete}
            onSubmitAudience={onSubmitAudience}
            onAddToExistingAudience={onAddToExistingAudience}
            selectedLocations={selectedLocations}
            updateTargetingLocation={updateTargetingLocation}
            isTargetingPreviewModalOpen={isTargetingPreviewModalOpen}
            targetingLocationsPreviewData={targetingLocationsPreviewData}
            closeTargetingPreviewModal={closeTargetingPreviewModal}
            bulkImportTargetingLocations={bulkImportTargetingLocations}
            selectTargetingLocationPreview={selectTargetingLocationPreview}
            deleteBulkLocation={deleteBulkLocation}
            targetingAudiences={targetingAudiences}
            selectedOrg={selectedOrg}
          />
        );
      case 'audience':
        return (
          <TargetingLocationAudiences
            toolbarContainer={toolbarContainer}
            title="Targeting Audiences"
            targetingAudiences={targetingAudiences}
            deleteAudienceLocation={deleteAudienceLocation}
            setAudienceAmplifyDefault={setAudienceAmplifyDefault}
            amplify={amplify}
          />
        );
      case 'region-group':
        return (
          <RegionGroup
            toolbarContainer={toolbarContainer}
            handleSubmitRegionGroup={handleSubmitRegionGroup}
            handleUpdateRegionGroup={handleUpdateRegionGroup}
            selectedOrg={selectedOrg}
            currentuser={currentuser}
            regionGroups={regionGroups}
            deleteRegionGroup={deleteRegionGroup}
            presetRegionGroups={presetRegionGroups}
            presetRegionGroupsLoading={presetRegionGroupsLoading}
            google={google}
          />
        );
      case 'deal-ids':
        return (
          <TargetingDealIds
            toolbarContainer={toolbarContainer}
            dealIds={dealIds}
            createDealId={createDealId}
            deleteDealIds={deleteDealIds}
            updateDealId={updateDealId}
          />
        );
      default:
        return null;
    }
  };

  const activeTabConfig = visibleTabs.find((t) => t.key === activeTab);
  const ActiveIcon = activeTabConfig ? activeTabConfig.icon : null;
  const activeCount = getTabCount(activeTab);

  return (
    <div>
      {/* Tab bar row: tabs left (or dropdown on mobile), toolbar right */}
      <div className="targeting-tabs">
        {useDropdown ? (
          /* ─── Mobile dropdown ─────────────────────────────────────── */
          <div className="targeting-tabs__dropdown" ref={dropdownRef}>
            <button
              type="button"
              className="targeting-tabs__dropdown-trigger"
              onClick={() => setDropdownOpen((prev) => !prev)}
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
            >
              <span className="targeting-tabs__dropdown-icon">
                {ActiveIcon && <ActiveIcon />}
              </span>
              <span className="targeting-tabs__dropdown-label">
                {activeTabConfig?.label}
              </span>
              {activeCount > 0 && (
                <span className="targeting-tabs__dropdown-count">{activeCount}</span>
              )}
              <DownOutlined className={`targeting-tabs__dropdown-chevron${dropdownOpen ? ' targeting-tabs__dropdown-chevron--open' : ''}`} />
            </button>
            {dropdownOpen && (
              <>
                <div
                  className="targeting-tabs__dropdown-backdrop"
                  onClick={() => setDropdownOpen(false)}
                />
                <ul className="targeting-tabs__dropdown-menu" role="listbox">
                  {visibleTabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.key;
                    const count = getTabCount(tab.key);

                    return (
                      <li
                        key={tab.key}
                        role="option"
                        aria-selected={isActive}
                        className={`targeting-tabs__dropdown-item${isActive ? ' targeting-tabs__dropdown-item--active' : ''}`}
                        onClick={() => {
                          setActiveTab(tab.key);
                          setDropdownOpen(false);
                        }}
                      >
                        <span className="targeting-tabs__dropdown-item-icon"><Icon /></span>
                        <span className="targeting-tabs__dropdown-item-label">{tab.label}</span>
                        {count > 0 && (
                          <span className="targeting-tabs__dropdown-item-count">{count}</span>
                        )}
                        {isActive && <span className="targeting-tabs__dropdown-check">✓</span>}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        ) : (
          /* ─── Desktop pill tabs ───────────────────────────────────── */
          <nav
            ref={navRef}
            className="targeting-tabs__nav"
            role="tablist"
            aria-label="Targeting sections"
            onKeyDown={handleKeyDown}
          >
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              const count = getTabCount(tab.key);

              return (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`targeting-panel-${tab.key}`}
                  id={`targeting-tab-${tab.key}`}
                  tabIndex={isActive ? 0 : -1}
                  className={`targeting-tabs__tab${isActive ? ' targeting-tabs__tab--active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <span className="targeting-tabs__tab-icon">
                    <Icon />
                  </span>
                  <span className="targeting-tabs__tab-label">{tab.label}</span>
                  {count > 0 && <span className="targeting-tabs__tab-count">{count}</span>}
                </button>
              );
            })}
          </nav>
        )}

        {/* Portal target — each tab's buttons render here */}
        <div ref={setToolbarContainer} className="targeting-tabs__toolbar" />
      </div>

      {/* Tab content */}
      <div
        role="tabpanel"
        id={`targeting-panel-${activeTab}`}
        aria-labelledby={`targeting-tab-${activeTab}`}
        className="targeting-tabs__panel"
      >
        {renderTabContent()}
      </div>
    </div>
  );
}
